import axios from 'axios';
import { toast } from 'sonner';

import { friendlyEntitlementMessage } from '@/lib/entitlement-error';
import { voiceErrorMessage } from '@/lib/voice-error-copy';
import { whatsappErrorMessage } from '@/lib/whatsapp-error-copy';

interface ApiDomainError {
    readonly statusCode?: number;
    readonly errorCode?: number;
    readonly code?: string;
    readonly message?: string;
}

interface ApiErrorBody {
    readonly error?: ApiDomainError | string;
    readonly message?: string | string[];
}

/**
 * Pulls a structured error envelope out of an unknown thrown value. Returns
 * undefined when the error is not from our API. Useful for callers that need
 * to branch on `error.code` (e.g. duplicate-submission → friendly screen).
 */
export function parseApiError(err: unknown): ApiErrorBody | undefined {
    if (axios.isAxiosError(err)) {
        const data = err.response?.data;
        return data && typeof data === 'object' ? (data as ApiErrorBody) : undefined;
    }
    return undefined;
}

/**
 * True when the request was rejected for lack of permission.
 *
 * Useful for list queries that would otherwise render an empty state: "no phone
 * numbers exist" and "you may not see the phone numbers" look identical to a
 * user, and only one of them is actionable by them.
 */
export function isForbiddenError(err: unknown): boolean {
    return axios.isAxiosError(err) && err.response?.status === 403;
}

/**
 * True when the target no longer exists (HTTP 404).
 *
 * For a DELETE that is the outcome the user asked for — a double click, a second
 * tab or a teammate got there first — so callers can treat it as done instead
 * of toasting the server's "X with id … not found".
 */
export function isNotFoundError(err: unknown): boolean {
    return axios.isAxiosError(err) && err.response?.status === 404;
}

/**
 * The backend's string error code (e.g. `'CAMPAIGN_NAME_ALREADY_EXISTS'`), when
 * the failure came from our API. Saves callers from re-narrowing
 * `parseApiError`'s `error: ApiDomainError | string` union every time they want
 * to give one specific domain error its own UX (an inline field error rather
 * than the shared toast, say).
 */
export function getApiErrorCode(err: unknown): string | undefined {
    const body = parseApiError(err);
    if (body?.error && typeof body.error === 'object') return body.error.code;
    return undefined;
}

function extractMessage(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;

    // Shape B (wrapped via ApiResponse): { error: { message, details? } }
    if (d.error && typeof d.error === 'object') {
        const inner = d.error as Record<string, unknown>;
        // Prefer per-field validation details over the generic top-level
        // message ("Validation failed") — they name the actual problem.
        const detail = extractDetailMessage(inner.details);
        if (detail) return detail;
        if (typeof inner.message === 'string') return inner.message;
        if (Array.isArray(inner.message) && typeof inner.message[0] === 'string') {
            return inner.message[0];
        }
    }
    // Shape A (raw NestJS exception): { message: string | string[] }
    if (typeof d.message === 'string') return d.message;
    if (Array.isArray(d.message) && typeof d.message[0] === 'string') return d.message[0];
    // Shape C (string error): { error: 'some message' }
    if (typeof d.error === 'string') return d.error;

    return null;
}

/** HTTP status of a failed request, when the failure carries a response. */
export function getApiErrorStatus(err: unknown): number | undefined {
    const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
    return typeof status === 'number' ? status : undefined;
}

/**
 * The server's own message for a failed request, for callers that show it
 * inline instead of toasting. Null when the failure has none to offer.
 */
export function getApiErrorMessage(err: unknown): string | null {
    const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
    return extractMessage(data);
}

/**
 * Flatten a validation `details` object into a single human sentence. Handles
 * both the field-definition shape (`{ columnSpan: [...] }`) and the record-write
 * shape (`{ fields: { name: [...] }, errors: [...] }`) — the latter's `fields`
 * map is preferred. Non-string-array values (e.g. the `errors` object array)
 * are ignored. Returns null when there's nothing specific to show.
 */
function extractDetailMessage(details: unknown): string | null {
    if (!details || typeof details !== 'object') return null;
    const record = details as Record<string, unknown>;
    const source =
        record.fields && typeof record.fields === 'object'
            ? (record.fields as Record<string, unknown>)
            : record;

    const messages: string[] = [];
    for (const value of Object.values(source)) {
        if (Array.isArray(value)) {
            for (const item of value) {
                if (typeof item === 'string') messages.push(item);
            }
        }
    }
    // Dedupe while preserving order; cap so the toast stays readable.
    const unique = [...new Set(messages)];
    if (unique.length === 0) return null;
    return unique.slice(0, 3).join('; ');
}

/**
 * Single-source-of-truth mutation error toaster. Replaces per-feature copies
 * (formerly under `features/{contacts,segments,join-links,channels}/utils/`).
 */
export function handleMutationError(error: unknown, fallback: string): void {
    if (axios.isAxiosError(error)) {
        // Entitlement/billing limit errors (50xxx) carry a technical token in
        // `message`; render friendly, resource-named copy from the error code
        // instead. Non-entitlement errors return undefined and fall through.
        const friendly = friendlyEntitlementMessage(error.response?.data);
        if (friendly) {
            toast.error(friendly);
            return;
        }
        // Voice/call errors (96xxx) state the problem but not the remedy, so
        // render actionable copy from the code. Same undefined-means-not-mine
        // contract as the entitlement map above.
        const voice = voiceErrorMessage(error.response?.data);
        if (voice) {
            toast.error(voice);
            return;
        }
        // WhatsApp channel errors (98xxx) — same undefined-means-not-mine
        // contract as the voice map above.
        const whatsapp = whatsappErrorMessage(error.response?.data);
        if (whatsapp) {
            toast.error(whatsapp);
            return;
        }
        const extracted = extractMessage(error.response?.data);
        toast.error(extracted ?? fallback);
        return;
    }
    if (error instanceof Error && error.message) {
        toast.error(error.message);
        return;
    }
    toast.error(fallback);
}
